<template>
    <div class="pin-form">
        <v-form @submit.prevent="enterPin">
            <label for="pin" class="">{{ $t('welcome.churchPin.title') }} <br><span class="font-weight-regular">{{ $t('welcome.churchPin.noPin') }} <br><i18n-t keypath="welcome.churchPin.adminSetup" tag="span"><template #link><a href="https://thejourneycurriculum.com/">thejourneycurriculum.com</a></template></i18n-t></span></label>
            <v-flex class="pin-container">
            <input type="text" maxlength="1" class="code" ref="code1" v-model="code1">
            <input type="text" maxlength="1" class="code" v-on:keyup="keymonitor" ref="code2" v-model="code2">
            <input type="text" maxlength="1" class="code" v-on:keyup="keymonitor" ref="code3" v-model="code3">
            <input type="text" maxlength="1" class="code" v-on:keyup="keymonitor" ref="code4" v-model="code4">
            </v-flex>
            <p v-if="feedback" class="bg-error feedback">{{ feedback }}</p>
            <v-btn
            :loading="loading"
            :disabled="loading"
            color="#A5D867"
            class="text-white mt-2"
            type="submit" 
            size="large"
            >
                {{ $t('loginScreen.startJourney') }}
                <template #loader>
                    <span class="custom-loader">
                        <v-icon icon="mdi-cached" />
                    </span>
                </template>
            </v-btn>
        </v-form>
    </div>
</template>

<script>
import firebase from 'firebase'
import db from '@/firebase/init'
import { localizedNamedLocation } from '@/helpers/localePath'
import { waitForUserAccess } from '@/helpers/userAccess'

export default {
    name: 'ChurchPin',
    data() {
        return {
            feedback: '',
            code: null,
            code1: null,
            code2: null,
            code3: null,
            code4: null,
            loading: false,
            memberExceededLimit: false,
            memberHasNotSignedUp: false,
            startjourney: null
        }
    },
    watch: {
        code1: function(val) {
            if(val && val.length > 0 && val.length != '') {
                // console.log("Fired! " + val, oldVal);
                this.$refs.code2.focus()
            }
        },
        code2: function(val) {
            if(val && val.length > 0 && val.length != '') {
                // console.log("Fired! " + val, oldVal);
                this.$refs.code3.focus()
            }
        },
        code3: function(val) {
            if(val && val.length > 0 && val.length != '') {
                // console.log("Fired! " + val, oldVal);
                this.$refs.code4.focus()
            }
        },
    },
    methods:{
        enterPin() {
            this.loading = true
            // we set a delay so the user sees something happen when they click the button
            setTimeout(async () => {

                // build the code from the inputs
                this.code = [this.code1, this.code2, this.code3, this.code4].join('')

                if(!this.code) {
                    this.feedback = this.$t('welcome.churchPin.required')
                    this.loading = false
                    return
                }
                this.feedback = ''

                // firestore.rules only let members read their own church, so a
                // function checks the PIN and sets the church (GitHub #132).
                let church
                try {
                    const res = await firebase
                        .app()
                        .functions('us-central1')
                        .httpsCallable('joinChurchByPin')({ pin: this.code })
                    church = res.data
                } catch (error) {
                    console.log('Error joining church by PIN: ', error)
                    this.feedback = this.$t('welcome.churchPin.checkFailed')
                    this.loading = false
                    return
                }
                if (!church?.joined) {
                    const reason = church?.reason
                    if (reason === 'not-verified') this.feedback = this.$t('welcome.churchPin.notVerified')
                    else if (reason === 'too-many-attempts') this.feedback = this.$t('welcome.churchPin.tooManyAttempts')
                    else this.feedback = this.$t('welcome.churchPin.notFound')
                    this.loading = false
                    return
                }

                try {
                    this.feedback = this.$t('welcome.churchPin.found')
                    const currentUser = this.$store.getters['UserData/getUser']

                    const userStateSnap = await db
                        .collection('userstate')
                        .where('cid', '==', church.cid)
                        .where('email', '==', currentUser.email)
                        .get()
                    if (userStateSnap.empty && !church.archived) this.memberHasNotSignedUp = true

                    this.$store.dispatch('AppData/resetNotifications', [])
                    this.$store.dispatch('AppData/resetUnits', [])
                    this.$store.dispatch('AppData/isRunning', false)

                    if (currentUser?.email) {
                        const snapshot = await db
                            .collection('users')
                            .where('email', '==', currentUser.email)
                            .get()
                        for (const snap of snapshot.docs) {
                            this.$store.dispatch('UserData/updateUser', {
                                cid: church.cid,
                                uid: snap.data().uid,
                            })
                        }
                        if (snapshot.docs.length > 1) {
                            snapshot.docs.forEach((snap) => {
                                if (snap.data().active == null) {
                                    snap.ref.delete()
                                }
                            })
                        }
                    }
                    // The new church's data is readable once userAccess has it
                    // (firestore.rules), so set the church after that.
                    const uid = church.uid || currentUser.uid
                    if (uid) await waitForUserAccess(uid, church.cid)

                    this.$store.dispatch('AppData/setChurchId', church.cid)
                    this.$store.dispatch('AppData/allChurches', [{
                        churchtitle: church.churchtitle,
                        cid: church.cid,
                        url: church.url,
                    }])
                    this.$emit('churchid', church.cid)
                    this.$router.push(localizedNamedLocation('Welcome', this.$route))
                } catch (error) {
                    console.log('Error saving church pin: ', error)
                    this.feedback = this.$t('welcome.churchPin.saveFailed')
                } finally {
                    this.loading = false
                }
            }, 1000);
        },
        keymonitor: function(event) {
            if(event.key == "Backspace") {
                event.target.previousElementSibling.focus()
            }
        },
    }
}
</script>

<style>
.pin-form {
    margin: 0 auto 50px;
    text-align: center;
}
/* Vuetify v-form uses flex row by default; stack label, PIN inputs, and button vertically */
.pin-form .v-form {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.pin-form label {
    /* font-size: 1.25rem;
    color:#000; */
    font-weight: 700;
    text-align: center;
    /* text-transform: uppercase; */
}
.pin-form h2 {
    font-size: 2em;
}
.pin-form .pin-container {
    margin: 10px;
}
.pin-form input.code {
    width: 36px !important;
    background-color: #fff !important;
    margin: 0 7px 0 0 !important;
    border: none;
    border-radius: 2px !important;
    box-shadow: 1px 1px 1px rgba(0,0,0,.10);
    /* box-shadow: none; */
    text-align: center !important;
    font-size: 32px;
    height: 100%;
    padding: 10px 4px;
}
.pin-form input.code:focus {
    outline: 2px dashed #a5d867;
}
.pin-form input[type=text]:not(.browser-default) {
    border-bottom:none;
    text-align: center;
    border-bottom: none;
    margin-left: 15px;
    color:#000;
    text-transform: uppercase;
}
.pin-form input:not(.browser-default):focus:not([readonly]) {
    border-bottom:none;
    box-shadow: none;
}
.pin-form p.feedback {
    margin-bottom: 0;
}
.pin-form .v-btn__content {
    font-weight: 700;
}
@media screen and (max-width: 55em) {
    .pin-form {
        width: 90%;
    }
}
</style>